import { Finding } from '@consigli/types';
import {
  addMonths,
  addWeeks,
  format,
  getISOWeek,
  isSameDay,
  isValid,
  parse,
  parseISO,
  startOfISOWeek,
  startOfMonth,
} from 'date-fns';

import {
  DOMAIN_PADDING_DAYS,
  MONTH_MIN_WIDTH_PX,
  MS_PER_DAY,
  TIMELINE_MIN_WIDTH_PX,
  type MilestoneRow,
  type MilestoneStatus,
  type WeekSegment,
} from './rfp-timeline-types';

export const parseMilestonePayload = (
  finding: Finding,
): { milestone: string; dateStr: string } | null => {
  const raw = finding.messageContext?.CUSTOM_MESSAGE;
  if (!raw || typeof raw === 'string' || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  if (typeof record.milestone !== 'string' || typeof record.date !== 'string') return null;
  return { milestone: record.milestone, dateStr: record.date };
};

// Formats the API may return for milestone dates, ordered most-specific first.
const MILESTONE_DATE_FORMATS = [
  'MMMM d, yyyy, h:mmaaa', // "May 20, 2026, 3:30pm"
  'MMMM d, yyyy, h:mm aaa', // "May 20, 2026, 3:30 pm"
  'MMMM d, yyyy', // "April 24, 2026"
  'MMMM yyyy', // "July 2026" → 1st of that month
  'MMM yyyy', // "Jul 2026" → 1st of that month
];

/** Format a parsed milestone Date for display in the table (e.g. "24 Apr 2026"). */
export const formatMilestoneDate = (date: Date): string => format(date, 'd MMM yyyy');

/**
 * Build the date string stored in message_context.CUSTOM_MESSAGE.date.
 * Matches MILESTONE_DATE_FORMATS so it can always be parsed back.
 */
export const buildMilestoneDateString = (dateStr: string, timeStr?: string): string => {
  const parsed = parse(dateStr, 'yyyy-MM-dd', new Date());
  if (timeStr) {
    const [hours, minutes] = timeStr.split(':').map(Number);
    parsed.setHours(hours, minutes);
    return format(parsed, 'MMMM d, yyyy, h:mmaaa');
  }
  return format(parsed, 'MMMM d, yyyy');
};

// "Aug / Sep 2026" or "Aug/Sep 2026" → parse the first month as the milestone date.
const MONTH_RANGE_PATTERN = /^(\w+)\s*\/\s*\w+\s+(\d{4})$/;

// "Q1 2027", "From Q2 2027 (indicative)" → quarter start month (Q1=Jan, Q2=Apr, Q3=Jul, Q4=Oct).
const QUARTER_PATTERN = /Q([1-4])\s+(\d{4})/;
const QUARTER_START_MONTH: Record<string, number> = { '1': 0, '2': 3, '3': 6, '4': 9 };

export const parseFlexibleDate = (value: string): Date | null => {
  const iso = parseISO(value);
  if (isValid(iso)) return iso;

  for (const fmt of MILESTONE_DATE_FORMATS) {
    const parsed = parse(value, fmt, new Date());
    if (isValid(parsed)) return parsed;
  }

  const rangeMatch = value.match(MONTH_RANGE_PATTERN);
  if (rangeMatch) {
    const synthetic = `${rangeMatch[1]} ${rangeMatch[2]}`;
    for (const fmt of ['MMMM yyyy', 'MMM yyyy']) {
      const parsed = parse(synthetic, fmt, new Date());
      if (isValid(parsed)) return parsed;
    }
  }

  const quarterMatch = value.match(QUARTER_PATTERN);
  if (quarterMatch) {
    const month = QUARTER_START_MONTH[quarterMatch[1]];
    const year = parseInt(quarterMatch[2], 10);
    return new Date(year, month, 1);
  }

  return null;
};

export const computeStatus = (date: Date, today: Date): MilestoneStatus => {
  if (isSameDay(date, today)) return 'active';
  return date.getTime() < today.getTime() ? 'past' : 'upcoming';
};

export const buildMonthTicks = (start: Date, end: Date): Date[] => {
  const ticks: Date[] = [];
  let cursor = startOfMonth(start);
  while (cursor.getTime() <= end.getTime()) {
    ticks.push(cursor);
    cursor = addMonths(cursor, 1);
  }
  return ticks;
};

/**
 * Build ISO 8601 week tick marks (Monday boundaries) within the visible domain.
 *
 * We use real ISO weeks rather than dividing each month into fixed quarters
 * because calendar months contain 4 or 5 (rarely 6) weeks — splitting by
 * quarters would produce labels that don't correspond to any standard week
 * and would be wrong whenever a month has 5 weeks.
 *
 * The first tick is the Monday immediately after the ISO-week boundary that
 * contains `domainStart`; subsequent ticks are each 7 days apart; the last
 * tick is the last Monday strictly before `domainEnd`.
 */
export const buildWeekTicks = (domainStart: Date, domainEnd: Date): Date[] => {
  const ticks: Date[] = [];
  let cursor = addWeeks(startOfISOWeek(domainStart), 1);
  while (cursor.getTime() < domainEnd.getTime()) {
    ticks.push(cursor);
    cursor = addWeeks(cursor, 1);
  }
  return ticks;
};

/**
 * Build labeled week segments from the Monday-aligned tick boundaries.
 *
 * Each segment covers the interval between two adjacent boundaries
 * (domain start → first Monday → … → domain end). Its label is the
 * ISO 8601 week number (1–53) of the segment's midpoint, so a month
 * that spans 5 weeks naturally shows 5 distinct week numbers rather
 * than the misleading W1–W4 that fixed-quarter splitting would give.
 */
export const buildWeekSegments = (
  weekTicks: Date[],
  domainStart: Date,
  domainEnd: Date,
): WeekSegment[] => {
  const boundaries = [domainStart, ...weekTicks, domainEnd];
  const segments: WeekSegment[] = [];
  for (let i = 0; i < boundaries.length - 1; i++) {
    const start = boundaries[i];
    const end = boundaries[i + 1];
    if (end.getTime() <= start.getTime()) continue;
    const midpoint = new Date((start.getTime() + end.getTime()) / 2);
    segments.push({ start, end, weekNumber: getISOWeek(midpoint) });
  }
  return segments;
};

export const positionPercent = (date: Date, start: Date, end: Date): number => {
  const totalMs = end.getTime() - start.getTime();
  if (totalMs <= 0) return 0;
  return Math.max(0, Math.min(100, ((date.getTime() - start.getTime()) / totalMs) * 100));
};

export const computeDomain = (rows: MilestoneRow[], today: Date): { start: Date; end: Date } => {
  if (rows.length === 0) {
    return { start: addMonths(today, -2), end: addMonths(today, 4) };
  }
  const timestamps = [today.getTime(), ...rows.map((row) => row.date.getTime())];
  const padMs = DOMAIN_PADDING_DAYS * MS_PER_DAY;
  return {
    start: new Date(Math.min(...timestamps) - padMs),
    end: new Date(Math.max(...timestamps) + padMs),
  };
};

export const computeMonthTickData = (domain: {
  start: Date;
  end: Date;
}): {
  monthTicks: Date[];
  weekTicks: Date[];
  weekSegments: WeekSegment[];
  spansMultipleYears: boolean;
  timelineMinWidth: number;
} => {
  const ticks = buildMonthTicks(domain.start, domain.end);
  const weekTicks = buildWeekTicks(domain.start, domain.end);
  const years = new Set(ticks.map((t) => t.getFullYear()));
  return {
    monthTicks: ticks,
    weekTicks,
    weekSegments: buildWeekSegments(weekTicks, domain.start, domain.end),
    spansMultipleYears: years.size > 1,
    timelineMinWidth: Math.max(TIMELINE_MIN_WIDTH_PX, ticks.length * MONTH_MIN_WIDTH_PX),
  };
};

export const countByStatus = (rows: MilestoneRow[]): Record<MilestoneStatus, number> => {
  const result: Record<MilestoneStatus, number> = { past: 0, active: 0, upcoming: 0 };
  for (const row of rows) result[row.status] += 1;
  return result;
};
